import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { detectCompoundFirstName } from "@/lib/name-utils";
import { isValidEmail, isValidPhone } from "@/lib/contact-validation";
import { Textarea } from "@/components/ui/textarea";
import { useCreateContact, type ContactRow } from "@/hooks/use-contacts";

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (contact: ContactRow) => void;
  defaults?: {
    lead_source?: string;
    entry_campaign?: string;
  };
  requirePhone?: boolean;
}

const INITIAL_FORM = { title: "", first_name: "", last_name: "", phone: "", email: "", city: "", notes: "" };

export function AddContactDialog({
  open,
  onOpenChange,
  onCreated,
  defaults,
  requirePhone = false,
}: AddContactDialogProps) {
  const createContact = useCreateContact();
  const [form, setForm] = useState(INITIAL_FORM);

  const emailError = !isValidEmail(form.email);
  const phoneError = !isValidPhone(form.phone);

  const canSubmit =
    form.first_name.trim().length > 0 &&
    (!requirePhone || form.phone.trim().length > 0) &&
    !emailError &&
    !phoneError &&
    !createContact.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const name = [form.first_name.trim(), form.last_name.trim()]
      .filter(Boolean)
      .join(" ");

    createContact.mutate(
      {
        name,
        title: form.title || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        city: form.city.trim() || null,
        notes: form.notes.trim() || null,
        stage: "prospect",
        heat_score: "cold",
        pilot_mode: "ai",
        validated_at: new Date().toISOString(),
        lead_source: defaults?.lead_source ?? "crm",
        entry_campaign: defaults?.entry_campaign ?? "lead_crm_actif",
      },
      {
        onSuccess: (data) => {
          setForm(INITIAL_FORM);
          onOpenChange(false);
          onCreated?.(data);
        },
      }
    );
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setForm(INITIAL_FORM);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouveau contact</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium">Civilité</label>
            <Select value={form.title || undefined} onValueChange={(v) => setForm((f) => ({ ...f, title: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="M. / Mme..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="M.">M.</SelectItem>
                <SelectItem value="Mme">Mme</SelectItem>
                <SelectItem value="M. et Mme">M. et Mme</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Prénom *</label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                placeholder="Prénom"
                autoFocus
              />
              {(() => {
                const detection = detectCompoundFirstName(form.first_name);
                if (!detection.isCompound || !detection.suggestion) return null;
                return (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-warning">
                    <span>Prénom composé détecté.</span>
                    <button
                      type="button"
                      className="underline font-medium hover:opacity-80"
                      onClick={() => setForm((f) => ({ ...f, first_name: detection.suggestion! }))}
                    >
                      Corriger en {detection.suggestion} ?
                    </button>
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="text-sm font-medium">Nom</label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                placeholder="Nom de famille"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">
                Téléphone {requirePhone && "*"}
              </label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+33 6 12 34 56 78"
                aria-invalid={phoneError}
              />
              {phoneError && (
                <p className="mt-1 text-xs text-destructive">Numéro de téléphone invalide.</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="email@exemple.fr"
                aria-invalid={emailError}
              />
              {emailError && (
                <p className="mt-1 text-xs text-destructive">Adresse email invalide.</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Ville</label>
            <Input
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="Paris, Lyon..."
            />
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Notes libres..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createContact.isPending ? "Création..." : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
